import { useCallback, useEffect, useState } from "react";
import { localStorageService } from "../lib/storage";
import type { PromptRecord } from "../lib/storage";

export function useLocalStorage() {
	const [prompts, setPrompts] = useState<PromptRecord[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	// 加载所有提示
	const loadPrompts = useCallback(async () => {
		try {
			setLoading(true);
			const data = await localStorageService.getAllPrompts();
			setPrompts(data);
			setError(null);
		} catch (err) {
			setError(
				err instanceof Error
					? err
					: new Error("Failed to load prompts"),
			);
		} finally {
			setLoading(false);
		}
	}, []);

	// 保存新的提示
	const savePrompt = useCallback(
		async (
			prompt: string,
			originalPrompt: string,
			image: string,
			modelType: string,
			tags?: string[],
		) => {
			try {
				const id = await localStorageService.savePrompt(
					prompt,
					originalPrompt,
					image,
					modelType,
					tags,
				);
				await loadPrompts(); // 重新加载以获取最新数据
				return id;
			} catch (err) {
				setError(
					err instanceof Error
						? err
						: new Error("Failed to save prompt"),
				);
				throw err;
			}
		},
		[loadPrompts],
	);

	// 删除提示
	const deletePrompt = useCallback(
		async (id: string) => {
			try {
				await localStorageService.deletePrompt(id);
				await loadPrompts(); // 重新加载以获取最新数据
			} catch (err) {
				setError(
					err instanceof Error
						? err
						: new Error("Failed to delete prompt"),
				);
				throw err;
			}
		},
		[loadPrompts],
	);

	// 按标签搜索提示
	const searchPrompts = useCallback(async (tags: string[]) => {
		try {
			setLoading(true);
			const results = await localStorageService.searchByTags(tags);
			setPrompts(results);
			setError(null);
		} catch (err) {
			setError(
				err instanceof Error
					? err
					: new Error("Failed to search prompts"),
			);
			throw err;
		} finally {
			setLoading(false);
		}
	}, []);

	// 清空所有数据
	const clearAll = useCallback(async () => {
		try {
			await localStorageService.clearAll();
			setPrompts([]);
			setError(null);
		} catch (err) {
			setError(
				err instanceof Error
					? err
					: new Error("Failed to clear prompts"),
			);
			throw err;
		}
	}, []);

	// 初始加载
	useEffect(() => {
		loadPrompts();
	}, [loadPrompts]);

	return {
		prompts,
		loading,
		error,
		savePrompt,
		deletePrompt,
		searchPrompts,
		clearAll,
		refresh: loadPrompts,
	};
}
